import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, Calculator, DollarSign, PieChart, AlertTriangle,
  CheckCircle2, ShieldCheck, BookOpen, Lock, RefreshCw, Info,
  TrendingDown, Sparkles, XCircle,
} from "lucide-react";

import ModuleShell from "@/components/lunimar/LunimarModuleShell";
import ModuleStepCard from "@/components/modules/ModuleStepCard";
import ModuleQuiz from "@/components/modules/ModuleQuiz";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription,
} from "@/components/ui/sheet";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";

import { useCurriculumProgress } from "@/hooks/useCurriculumProgress";
import { useModuleRewards } from "@/hooks/useModuleRewards";
import { useAccounts } from "@/hooks/useAccounts";
import { useAuth } from "@/hooks/useAuth";
import { useQAOverride } from "@/hooks/useQAOverride";
import { useAccessTier } from "@/hooks/useAccessTier";
import { supabase } from "@/integrations/supabase/client";
import {
  newAttemptId, startModuleAttempt, commitModuleAttempt, voidModuleAttempt,
  trackModule, trackAccountEffect,
} from "@/lib/learning/telemetry";
import { BUDGET_EVENTS, MODULE_CONCEPT_TAGS } from "@/lib/learning/moduleEvents";
import { useSfx } from "@/hooks/useSfx";
import { useSceneMusic } from "@/hooks/useSceneMusic";
import { useInstructorVoice, useTriggerInstructorVoice, usePreloadInstructorVoice } from "@/hooks/useInstructorVoice";
import { useLaunchModuleAccess } from "@/components/launch/LaunchModuleGate";
import RetakeModuleDialog from "@/components/modules/RetakeModuleDialog";
import type { AnyRow } from "@/lib/rowTypes";
import { useModuleRetake } from "@/hooks/useModuleRetake";

const BUDGET_VOICE_PRELOAD: string[] = [
  "budget.intro",
  "budget.postIncomeSelected",
  "budget.deductionsExplained",
  "budget.preAllocation",
  "budget.hint.noEmergency",
  "budget.consequence.surpriseExpense",
  "budget.completion",
  "budget.success.strongBudget",
];
import type { MusicKey } from "@/lib/audio/musicMap";

/* ========================================================================== */
/* QA / Completion logic                                                       */
/* --------------------------------------------------------------------------- */
/* - On mount we look up module_completions for budget_builder. If passed=true */
/*   we render the results summary and disable replay (QA admins bypass).      */
/* - During a run we create one budget_module_attempts row up-front and        */
/*   update it across steps. The row stores gross/take-home/allocations/       */
/*   surprise/adjustment/outcome.                                              */
/* - Account credits are NEVER applied until the learner passes the quiz       */
/*   (4/5). On pass we mark the attempt passed=true and call the server RPC    */
/*   apply_budget_module_attempt which credits accounts idempotently. Locked   */
/*   accounts still accrue balance.                                            */
/* - Failing the quiz voids the attempt — no credit, no module_completions     */
/*   row (ModuleQuiz already skips writing on fail/QA), learner is forced to   */
/*   retake. We do not delete the attempt row so we have an audit trail.       */
/* ========================================================================== */

type Step =
  | "intro"
  | "income"
  | "deductions"
  | "allocate"
  | "review_budget"
  | "stress"      // surprise reveal
  | "adjust"
  | "outcome"
  | "quiz"
  | "summary";

const STEPS: Step[] = [
  "intro", "income", "deductions", "allocate", "review_budget",
  "stress", "adjust", "outcome", "quiz", "summary",
];

/* ---- Deductions (educational estimates, not a tax calculator) ------------ */

type Deduction = {
  key: string;
  label: string;
  rate: number; // share of gross
  description: string;
  why: string;
  usedFor: string;
};

const DEDUCTIONS: Deduction[] = [
  {
    key: "fed_tax",
    label: "Federal income tax",
    rate: 0.10,
    description: "Tax paid to the federal government, withheld from each paycheck.",
    why: "The IRS requires employers to withhold an estimate of what you'll owe at tax time.",
    usedFor: "Funds national programs — defense, transportation, education grants, federal services.",
  },
  {
    key: "state_tax",
    label: "State income tax",
    rate: 0.04,
    description: "Tax paid to your state government.",
    why: "Most states levy income tax; rates vary widely. A few states have none.",
    usedFor: "Funds state programs — schools, roads, public safety, Medicaid match.",
  },
  {
    key: "ss",
    label: "Social Security (FICA)",
    rate: 0.062,
    description: "Payroll tax of 6.2% on wages.",
    why: "Mandatory contribution toward the federal retirement & disability system.",
    usedFor: "Pays current retirees, disabled workers, and survivors. You earn credits toward future benefits.",
  },
  {
    key: "medicare",
    label: "Medicare (FICA)",
    rate: 0.0145,
    description: "Payroll tax of 1.45% on wages.",
    why: "Mandatory contribution to the federal health insurance program for older adults.",
    usedFor: "Funds hospital insurance for Americans 65+ and certain people with disabilities.",
  },
  {
    key: "benefits",
    label: "Health benefits",
    rate: 0.05,
    description: "Your share of employer-sponsored health insurance, dental, vision.",
    why: "Many full-time jobs offer benefits; employees typically pay part of the premium.",
    usedFor: "Pays the insurance carrier so you can use coverage for visits, prescriptions, and care.",
  },
];

const DEDUCTION_NOTE =
  "Numbers shown are educational estimates, not your real tax situation.";

function computeDeductions(gross: number) {
  const lines = DEDUCTIONS.map((d) => ({ key: d.key, label: d.label, amount: Math.round(gross * d.rate) }));
  const total = lines.reduce((s, l) => s + l.amount, 0);
  const takeHome = Math.max(0, gross - total);
  return { lines, total, takeHome };
}

/* ---- Categories for budget allocation ------------------------------------ */

type CategoryKey =
  | "rent" | "food" | "transport" | "phone" | "insurance"
  | "entertainment" | "subscriptions" | "emergency" | "savings";

type Category = {
  key: CategoryKey;
  label: string;
  bucket: "needs" | "wants" | "savings";
  bucketLabel: string;
  description: string;
};

const CATEGORIES: Category[] = [
  { key: "rent",          label: "Rent / housing",  bucket: "needs",   bucketLabel: "Need",    description: "Place to live — rent, mortgage, basic utilities." },
  { key: "food",          label: "Food / groceries",bucket: "needs",   bucketLabel: "Need",    description: "Groceries and essential meals." },
  { key: "transport",     label: "Transportation",  bucket: "needs",   bucketLabel: "Need",    description: "Bus pass, gas, basic car costs." },
  { key: "phone",         label: "Phone / internet",bucket: "needs",   bucketLabel: "Need",    description: "Connectivity for work, school, safety." },
  { key: "insurance",     label: "Insurance",       bucket: "needs",   bucketLabel: "Need",    description: "Renters / auto insurance premiums." },
  { key: "entertainment", label: "Entertainment",   bucket: "wants",   bucketLabel: "Want",    description: "Going out, hobbies, fun spending." },
  { key: "subscriptions", label: "Subscriptions",   bucket: "wants",   bucketLabel: "Want",    description: "Streaming, apps, recurring extras." },
  { key: "emergency",     label: "Emergency fund",  bucket: "savings", bucketLabel: "Save",    description: "Cash reserve for unexpected costs." },
  { key: "savings",       label: "Long-term savings",bucket: "savings",bucketLabel: "Save",    description: "Future goals — moving, school, investing." },
];

type Allocations = Record<CategoryKey, number>;
const ZERO_ALLOC: Allocations = {
  rent: 0, food: 0, transport: 0, phone: 0, insurance: 0,
  entertainment: 0, subscriptions: 0, emergency: 0, savings: 0,
};

/* American-average share of take-home pay for fixed categories.
   Learners only choose how to split the remainder between emergency fund
   and long-term savings. Sums to 0.77; the leftover 0.23 is theirs to split. */
const AVG_SHARE: Partial<Record<CategoryKey, number>> = {
  rent: 0.33,
  food: 0.12,
  transport: 0.16,
  phone: 0.03,
  insurance: 0.06,
  entertainment: 0.05,
  subscriptions: 0.02,
};
const USER_KEYS: CategoryKey[] = ["emergency", "savings"];

function computeFixedAlloc(takeHome: number): Allocations {
  const next: Allocations = { ...ZERO_ALLOC };
  for (const [k, share] of Object.entries(AVG_SHARE)) {
    next[k as CategoryKey] = Math.round(takeHome * (share as number));
  }
  return next;
}

/* ---- Glossary (in-module, never navigates away) -------------------------- */

const GLOSSARY: { term: string; def: string }[] = [
  { term: "Gross pay", def: "Your total earnings before any taxes or deductions are taken out." },
  { term: "Take-home pay (net pay)", def: "The money you actually receive after taxes and deductions." },
  { term: "Withholding", def: "Money your employer holds back from each paycheck to send to the government on your behalf." },
  { term: "FICA", def: "Federal Insurance Contributions Act — payroll taxes for Social Security and Medicare." },
  { term: "Emergency fund", def: "Cash reserve set aside specifically for unplanned expenses." },
  { term: "Needs vs wants", def: "Needs are required to live and work. Wants are nice-to-have." },
  { term: "50 / 30 / 20", def: "A budgeting guideline: roughly 50% needs, 30% wants, 20% savings of take-home pay." },
];

function KeyTermsButton() {
  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2 text-xs">
          <BookOpen className="h-3.5 w-3.5" /> Key Terms
        </Button>
      </SheetTrigger>
      <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Key Terms</SheetTitle>
          <SheetDescription>Quick definitions — closing this returns you to the same step.</SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-3">
          {GLOSSARY.map((g) => (
            <div key={g.term} className="rounded-2xl border border-border/60 bg-card/50 p-3">
              <p className="text-sm font-semibold text-foreground">{g.term}</p>
              <p className="mt-1 text-xs text-muted-foreground">{g.def}</p>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* ========================================================================== */

const BudgetBuilderModule = () => {
  const navigate = useNavigate();
  const { tier: accessTier } = useAccessTier();
  const betaModuleUnlocked = useLaunchModuleAccess("budget_builder");
  const isBetaModuleExperience = accessTier !== "admin" || betaModuleUnlocked;
  const backHref = isBetaModuleExperience ? "/dashboard" : "/curriculum";
  const backLabel = "Back to Dashboard";
  const { user } = useAuth();
  const { isQAOverride } = useQAOverride();
  const { moduleGates, loading: progressLoading } = useCurriculumProgress();
  const { completeModule } = useModuleRewards();
  const { accounts, refetch: refetchAccounts } = useAccounts();
  const { playSfx } = useSfx();
  const { playMoment: playVoiceMoment } = useInstructorVoice();

  /* completion gate (revisit shows summary, not restart) */
  const [previouslyCompleted, setPreviouslyCompleted] = useState<boolean | null>(null);
  const [previousAttempt, setPreviousAttempt] = useState<AnyRow | null>(null);
  const [retakeDialogOpen, setRetakeDialogOpen] = useState(false);
  const { reverseAndReset, pending: retakePending } = useModuleRetake();

  const handleConfirmModuleRetake = async () => {
    const result = await reverseAndReset("budget_builder");
    if (result) {
      setRetakeDialogOpen(false);
      window.location.reload();
    }
  };

  useEffect(() => {
    if (!user) return;
    let active = true;
    (async () => {
      const { data: comp } = await supabase
        .from("module_completions")
        .select("passed, completed_at")
        .eq("user_id", user.id)
        .eq("module_key", "budget_builder")
        .maybeSingle();
      if (!active) return;
      const passed = Boolean((comp)?.passed);
      setPreviouslyCompleted(passed);
      if (passed) {
        const { data: att } = await supabase
          .from("budget_module_attempts")
          .select("*")
          .eq("user_id", user.id)
          .eq("passed", true)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (active) setPreviousAttempt(att);
      }
    })();
    return () => { active = false; };
  }, [user]);

  /* step state */
  const [step, setStep] = useState<Step>("intro");
  const budgetSceneKey: MusicKey = step === "intro"
    ? "moduleMissionStart"
    : step === "summary"
      ? "moduleCompletion"
      : step === "outcome" || step === "stress"
        ? "reflectionState"
        : "simWalletFlow";
  useSceneMusic(budgetSceneKey);
  // Voice: module intro lands on the first paint of the intro step.
  useTriggerInstructorVoice("budget.intro", step === "intro");
  // Beta v2: per-step mentor moments. `useTriggerInstructorVoice` fires
  // once when its condition first becomes true, so these never repeat
  // on rerender / scroll, and they naturally space themselves across
  // step transitions (learner-paced) instead of stacking back-to-back.
  useTriggerInstructorVoice("budget.postIncomeSelected", step === "deductions");
  useTriggerInstructorVoice("budget.deductionsExplained", step === "allocate");
  // `budget.preAllocation` stays in the allowlist for future wiring; it
  // would overlap with `budget.deductionsExplained` on the same step
  // entry, so we keep voice density at one line per allocation phase.
  useTriggerInstructorVoice("budget.consequence.surpriseExpense", step === "stress");
  usePreloadInstructorVoice(BUDGET_VOICE_PRELOAD);
  const [gross, setGross] = useState(1500);
  const [committedGross, setCommittedGross] = useState<number | null>(null);
  const [deductionsRevealed, setDeductionsRevealed] = useState(0);
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const [alloc, setAlloc] = useState<Allocations>(ZERO_ALLOC);
  const [allocCommitted, setAllocCommitted] = useState(false);
  const [surpriseAmount, setSurpriseAmount] = useState(0);
  const [adjustment, setAdjustment] = useState<string | null>(null);
  const [outcomeData, setOutcomeData] = useState<AnyRow>(null);
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const completionFiredRef = useRef(false);
  const telemetryAttemptRef = useRef<string>(newAttemptId("budget_builder"));
  const telemetryStartedRef = useRef(false);
  const ensureTelemetryStart = () => {
    if (telemetryStartedRef.current) return;
    telemetryStartedRef.current = true;
    void startModuleAttempt({
      moduleSlug: "budget_builder",
      attemptId: telemetryAttemptRef.current,
      conceptTags: MODULE_CONCEPT_TAGS.budget_builder,
    });
  };

  const deductions = useMemo(
    () => committedGross ? computeDeductions(committedGross) : null,
    [committedGross]
  );
  const takeHome = deductions?.takeHome ?? 0;

  const totalAllocated = useMemo(
    () => (Object.values(alloc) as number[]).reduce((s, v) => s + (Number(v) || 0), 0),
    [alloc]
  );
  const remaining = takeHome - totalAllocated;
  const bucketTotals = useMemo(() => {
    const out = { needs: 0, wants: 0, savings: 0 };
    for (const c of CATEGORIES) out[c.bucket] += Number(alloc[c.key]) || 0;
    return out;
  }, [alloc]);
  const emergencyFund = Number(alloc.emergency) || 0;

  /* Prefill fixed (non-savings) categories with US averages once we hit
     the allocate step and have a take-home figure. Re-runs if take-home
     changes or if the fixed slots are still empty. */
  useEffect(() => {
    if (step !== "allocate" || takeHome <= 0) return;
    const fixedKeys = Object.keys(AVG_SHARE) as CategoryKey[];
    const needsFill = fixedKeys.every((k) => !alloc[k]);
    if (!needsFill) return;
    const filled = computeFixedAlloc(takeHome);
    setAlloc((prev) => ({ ...prev, ...filled }));
  }, [step, takeHome, alloc]);

  /* Auto-reveal deductions one by one (no manual click). Respects
     prefers-reduced-motion by jumping to fully revealed instantly. */
  useEffect(() => {
    if (step !== "deductions" || !deductions) return;
    if (deductionsRevealed >= deductions.lines.length) return;
    if (prefersReducedMotion) {
      setDeductionsRevealed(deductions.lines.length);
      return;
    }
    const delay = deductionsRevealed === 0 ? 500 : 600;
    const t = setTimeout(() => {
      playSfx("auto_deduction_tick");
      setDeductionsRevealed((n) => n + 1);
    }, delay);
    return () => clearTimeout(t);
  }, [step, deductions, deductionsRevealed, prefersReducedMotion, playSfx]);

  /* Reset reveal counter whenever the learner re-enters the deductions step. */
  useEffect(() => {
    if (step === "deductions") setDeductionsRevealed(0);
  }, [step]);

  /* gate / loading */
  const gate = moduleGates.find((g) => g.moduleKey === "budget_builder");
  // Beta users reach this page only when LaunchModuleGate has confirmed unlock,
  // so bypass curriculum prerequisite gating for them.
  const isLocked = isBetaModuleExperience ? false : !gate?.isUnlocked;
  const lockMsg = gate?.missingPrereqs?.length
    ? `Complete these courses first: ${gate.missingPrereqs.join(", ")}`
    : "Complete the Saving & Budgeting course to unlock this module.";

  if (progressLoading || previouslyCompleted === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <RefreshCw className="h-4 w-4 animate-spin" /> Loading module…
        </div>
      </div>
    );
  }

  /* ---------- COMPLETED REVISIT — show summary only --------------------- */
  if (previouslyCompleted && !isQAOverride) {
    return (
      <ModuleShell
        realmKey="budget_builder"
        title="Budget Builder"
        stepIndex={STEPS.length - 1}
        totalSteps={STEPS.length}
        isLocked={false}
        lockMessage=""
        isCompleted={false}
        stepKey="completed_summary"
      >
        <div className="space-y-6">
          <div className="text-center space-y-3">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10">
              <CheckCircle2 className="h-8 w-8 text-primary" />
            </div>
            <h2 className="font-heading text-xl font-bold text-foreground">Module Complete</h2>
            <p className="text-sm text-muted-foreground">
              You've already passed Budget Builder. Replays are disabled to keep your account
              balances honest — you can revisit the summary anytime.
            </p>
          </div>
          {previousAttempt && (
            <div className="rounded-2xl border border-border bg-card/50 p-4 space-y-2 text-sm">
              <SummaryRow label="Gross income chosen" value={`$${previousAttempt.gross_income}`} />
              <SummaryRow label="Take-home pay" value={`$${previousAttempt.take_home}`} />
              <SummaryRow label="Surprise expense" value={`$${previousAttempt.surprise_amount}`} />
              <SummaryRow label="Adjustment choice" value={previousAttempt.adjustment_choice ?? "—"} />
              <SummaryRow
                label="Account credits applied"
                value={previousAttempt.applied ? "Yes" : "Pending"}
              />
            </div>
          )}
          <div className="flex flex-col gap-3">
            <Button onClick={() => navigate("/finances")} className="w-full gap-2">
              View Finances <ArrowRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" onClick={() => navigate(backHref)}>{backLabel}</Button>
            <Button
              variant="ghost"
              onClick={() => setRetakeDialogOpen(true)}
              className="w-full text-muted-foreground hover:text-destructive"
            >
              Retake Module
            </Button>
          </div>
        </div>
        <RetakeModuleDialog
          open={retakeDialogOpen}
          onOpenChange={setRetakeDialogOpen}
          moduleDisplayName="Budget Builder"
          onConfirm={handleConfirmModuleRetake}
          pending={retakePending}
        />
      </ModuleShell>
    );
  }

  /* ---------- step actions ---------------------------------------------- */

  const handleConfirmIncome = async () => {
    playSfx("decision_lock");
    ensureTelemetryStart();
    void trackModule({
      eventType: BUDGET_EVENTS.INCOME_SELECTED,
      moduleSlug: "budget_builder",
      attemptId: telemetryAttemptRef.current,
      sourceId: `bb:${telemetryAttemptRef.current}:income`,
      metadata: { gross_income: gross },
    });
    setCommittedGross(gross);
    setStep("deductions");
    setDeductionsRevealed(0);
  };

  const handleDeductionsContinue = () => {
    playSfx("soft_swoosh");
    void trackModule({
      eventType: BUDGET_EVENTS.DEDUCTIONS_VIEWED,
      moduleSlug: "budget_builder",
      attemptId: telemetryAttemptRef.current,
      sourceId: `bb:${telemetryAttemptRef.current}:deductions`,
      metadata: {
        gross_income: committedGross,
        deductions_total: deductions?.total ?? null,
        take_home_pay: takeHome,
      },
    });
    void trackModule({
      eventType: BUDGET_EVENTS.ALLOCATION_STARTED,
      moduleSlug: "budget_builder",
      attemptId: telemetryAttemptRef.current,
      sourceId: `bb:${telemetryAttemptRef.current}:alloc-start`,
    });
    // Prefill fixed categories with American averages so learners
    // only decide emergency fund vs. long-term savings.
    setAlloc((prev) => ({ ...computeFixedAlloc(takeHome), emergency: prev.emergency, savings: prev.savings }));
    setStep("allocate");
  };

  const handleAllocCommit = async () => {
    if (totalAllocated <= 0) {
      toast.error("Allocate at least one category before continuing.");
      return;
    }
    if (totalAllocated > takeHome) {
      toast.error(`Over budget by $${totalAllocated - takeHome}. Cut something or accept simulated debt later.`);
      void trackModule({
        eventType: BUDGET_EVENTS.OVERFUNDED,
        moduleSlug: "budget_builder",
        attemptId: telemetryAttemptRef.current,
        metadata: { over_budget_amount: totalAllocated - takeHome },
      });
      return;
    }
    playSfx("decision_lock");
    setAllocCommitted(true);
    // Delayed wallet update sequel — allocation commit lands as a balance change.
    setTimeout(() => playSfx("wallet_update"), 350);
    void trackModule({
      eventType: BUDGET_EVENTS.ALLOCATION_SAVED,
      moduleSlug: "budget_builder",
      attemptId: telemetryAttemptRef.current,
      sourceId: `bb:${telemetryAttemptRef.current}:alloc-saved`,
      metadata: {
        gross_income: committedGross,
        take_home_pay: takeHome,
        needs_allocation: bucketTotals.needs,
        wants_allocation: bucketTotals.wants,
        savings_allocation: bucketTotals.savings,
        emergency_fund_allocation: emergencyFund,
      },
    });
    // Create attempt row up-front so we have a stable id for later updates.
    if (user && committedGross) {
      const { data, error } = await supabase
        .from("budget_module_attempts")
        .insert({
          user_id: user.id,
          module_key: "budget_builder",
          gross_income: committedGross,
          take_home: takeHome,
          deductions: deductions?.lines ?? [],
          allocations: alloc,
        })
        .select("id")
        .single();
      if (!error && data) setAttemptId((data).id);
    }
    // Voice (hint): no emergency cushion → Lumi gently nudges.
    // Fires after the allocation is locked, never while sliders move.
    if (emergencyFund <= 0) playVoiceMoment("budget.hint.noEmergency");
    setStep("review_budget");
  };

  // "Continue" button — surprise reveal is intentionally hidden.
  const handleContinueToStress = () => {
    playSfx("soft_swoosh");
    // Surprise scales with take-home so consequences are real but proportionate.
    const surprise = Math.max(80, Math.round(takeHome * 0.18));
    setSurpriseAmount(surprise);
    void trackModule({
      eventType: BUDGET_EVENTS.SURPRISE_TRIGGERED,
      moduleSlug: "budget_builder",
      attemptId: telemetryAttemptRef.current,
      sourceId: `bb:${telemetryAttemptRef.current}:surprise`,
      metadata: { surprise_event_type: "car_repair", surprise_event_cost: surprise },
    });
    setStep("stress");
  };

  const handleChooseAdjustment = async (choice: string) => {
    playSfx("decision_lock");
    setAdjustment(choice);

    // Compute final outcome based on choice + emergency reserves.
    const emergencyAvailable = emergencyFund;
    let debt = 0;
    let netEmergency = emergencyAvailable;
    let netSavings = Number(alloc.savings) || 0;
    let netWants = bucketTotals.wants;
    const summary: string[] = [];

    if (choice === "emergency") {
      if (emergencyAvailable >= surpriseAmount) {
        netEmergency = emergencyAvailable - surpriseAmount;
        summary.push("Emergency fund absorbed the shock — budget stays intact.");
      } else {
        const shortfall = surpriseAmount - emergencyAvailable;
        netEmergency = 0;
        debt = shortfall;
        summary.push(`Emergency fund covered $${emergencyAvailable}; remaining $${shortfall} went to debt.`);
      }
    } else if (choice === "cut_wants") {
      if (netWants >= surpriseAmount) {
        netWants -= surpriseAmount;
        summary.push("Cut wants this month to cover the cost — savings & emergency untouched.");
      } else {
        const shortfall = surpriseAmount - netWants;
        netWants = 0;
        debt = shortfall;
        summary.push(`Wants covered $${netWants}; remaining $${shortfall} went to debt.`);
      }
    } else if (choice === "reduce_savings") {
      if (netSavings >= surpriseAmount) {
        netSavings -= surpriseAmount;
        summary.push("Paused long-term savings this month — future goals delayed.");
      } else {
        const shortfall = surpriseAmount - netSavings;
        netSavings = 0;
        debt = shortfall;
        summary.push(`Savings covered $${netSavings}; remaining $${shortfall} went to debt.`);
      }
    } else if (choice === "debt") {
      debt = surpriseAmount;
      summary.push("Put it on credit — fast today, expensive later (interest accrues).");
    } else if (choice === "split") {
      const third = Math.ceil(surpriseAmount / 3);
      const eUse = Math.min(third, netEmergency);
      const wUse = Math.min(third, netWants);
      const sUse = Math.min(surpriseAmount - eUse - wUse, netSavings);
      netEmergency -= eUse;
      netWants -= wUse;
      netSavings -= sUse;
      const remainder = surpriseAmount - eUse - wUse - sUse;
      if (remainder > 0) debt = remainder;
      summary.push(`Split across emergency ($${eUse}), wants ($${wUse}), savings ($${sUse})${remainder ? `, debt ($${remainder})` : ""}.`);
    }

    const account_credits = {
      // Money actually saved into long-term savings & emergency this cycle goes to real accounts.
      checking: Math.max(0, takeHome - bucketTotals.needs - netWants - netEmergency - netSavings),
      emergency: netEmergency,
      savings: netSavings,
    };

    const outcome = {
      surprise_amount: surpriseAmount,
      debt,
      net_emergency: netEmergency,
      net_savings: netSavings,
      net_wants: netWants,
      summary,
      account_credits,
    };
    setOutcomeData(outcome);
    if (debt > 0) {
      playSfx("money_loss");
    } else {
      // Reveal of the final post-surprise outcome screen
      playSfx("result_reveal");
    }
    void trackModule({
      eventType: BUDGET_EVENTS.ADJUSTMENT,
      moduleSlug: "budget_builder",
      attemptId: telemetryAttemptRef.current,
      metadata: { adjustment_choice: choice, debt_created: debt, surprise_event_cost: surpriseAmount },
    });
    void trackModule({
      eventType: BUDGET_EVENTS.SURPRISE_RESOLVED,
      moduleSlug: "budget_builder",
      attemptId: telemetryAttemptRef.current,
      metadata: { adjustment_choice: choice, debt_created: debt },
    });
    void trackModule({
      eventType: BUDGET_EVENTS.FINAL_OUTCOME,
      moduleSlug: "budget_builder",
      attemptId: telemetryAttemptRef.current,
      metadata: {
        final_saved_amount: outcome.net_savings + outcome.net_emergency,
        final_lost_amount: debt,
        account_effect_status: "draft",
      },
    });
    // Draft account effects — committed only on quiz pass.
    void trackAccountEffect({
      moduleSlug: "budget_builder",
      attemptId: telemetryAttemptRef.current,
      eventType: "account_balance_changed",
      status: "draft",
      account: "savings",
      amount: outcome.account_credits.savings,
    });
    void trackAccountEffect({
      moduleSlug: "budget_builder",
      attemptId: telemetryAttemptRef.current,
      eventType: "account_balance_changed",
      status: "draft",
      account: "emergency",
      amount: outcome.account_credits.emergency,
    });

    if (attemptId) {
      await supabase
        .from("budget_module_attempts")
        .update({
          surprise_amount: surpriseAmount,
          adjustment_choice: choice,
          outcome,
        })
        .eq("id", attemptId);
    }
    setStep("outcome");
  };

  const handleQuizPass = async () => {
    if (completionFiredRef.current) return;
    completionFiredRef.current = true;
    setApplying(true);
    void trackModule({
      eventType: BUDGET_EVENTS.QUIZ_PASSED,
      moduleSlug: "budget_builder",
      attemptId: telemetryAttemptRef.current,
      correctness: true,
    });

    try {
      // Only on PASS: mark attempt passed and apply credits via RPC (idempotent).
      if (attemptId && !isQAOverride) {
        // `passed` is money-bearing: the server grants it only after it has
        // recorded a passing quiz for this module.
        await completeModule("budget_builder");
        await supabase.rpc("pass_budget_module_attempt", { p_attempt_id: attemptId });

        const { error } = await supabase.rpc("apply_budget_module_attempt", {
          p_attempt_id: attemptId,
        });

        if (error) {
          console.warn("[budget_builder] apply attempt failed:", error.message);
          toast.error("Could not apply account credits. Contact support.");
        } else {
          await refetchAccounts();
          toast.success("Account balances updated from your budget.");
        }
      }
      await completeModule("budget_builder");
      playSfx("module_complete");
      // Voice: completion. Strong-budget outcome (no debt, real cushion
      // left) earns the supportive "breathing room" line; otherwise we
      // fall back to the essential completion line. Only one fires.
      const strongBudget =
        !outcomeData?.debt &&
        ((outcomeData?.net_savings ?? 0) + (outcomeData?.net_emergency ?? 0)) > 0;
      playVoiceMoment(strongBudget ? "budget.success.strongBudget" : "budget.completion");
      void commitModuleAttempt({
        moduleSlug: "budget_builder",
        attemptId: telemetryAttemptRef.current,
        metadata: {
          take_home_pay: takeHome,
          final_saved_amount: (outcomeData?.net_savings ?? 0) + (outcomeData?.net_emergency ?? 0),
          final_lost_amount: outcomeData?.debt ?? 0,
        },
      });
    } finally {
      setApplying(false);
      setStep("summary");
    }
  };

  /* ---------- gating still works for normal users ----------------------- */

  return (
    <ModuleShell
      realmKey="budget_builder"
      title="Budget Builder"
      stepIndex={STEPS.indexOf(step)}
      totalSteps={STEPS.length}
      isLocked={isLocked}
      lockMessage={lockMsg}
      isCompleted={false}
      stepKey={step}
    >
      <TooltipProvider delayDuration={150}>
        <div className="mb-3 flex justify-end">
          <KeyTermsButton />
        </div>

        {/* INTRO */}
        {step === "intro" && (
          <ModuleStepCard
            icon={<Calculator className="h-8 w-8 text-primary" />}
            title="Build Your Monthly Budget"
            body="You'll pick a monthly income, watch real deductions come out, allocate take-home pay across real categories, and survive a surprise expense. Your choices affect your simulated account balances — but only if you pass the knowledge check."
            highlights={[
              "Every decision has a consequence",
              "No correct answers are shown before you commit",
              "Account credits only apply after you pass 4/5 on the quiz",
            ]}
            action={
              <Button onClick={() => { playSfx("module_start"); setStep("income"); }} className="w-full gap-2">
                Let's Begin <ArrowRight className="h-4 w-4" />
              </Button>
            }
          />
        )}

        {/* INCOME */}
        {step === "income" && (
          <div className="space-y-6">
            <ModuleStepCard
              icon={<DollarSign className="h-8 w-8 text-primary" />}
              title="Choose your monthly gross income"
              body="Pick a realistic monthly gross paycheck for this simulation — anywhere from $1 to $3,000. This is your pay BEFORE taxes and deductions."
              subtext="Real life rarely picks round numbers. Pick what feels true to you."
              action={<div />}
            />
            <div className="rounded-2xl border border-border bg-card/50 p-4 space-y-4">
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">Monthly gross</p>
                  <p className="font-heading text-3xl font-bold text-foreground">${gross.toLocaleString()}</p>
                </div>
                <Input
                  type="number"
                  min={1}
                  max={3000}
                  value={gross}
                  onChange={(e) => {
                    const v = Math.max(1, Math.min(3000, Number(e.target.value) || 1));
                    setGross(v);
                  }}
                  className="w-28 text-right"
                />
              </div>
              <Slider
                value={[gross]}
                min={1}
                max={3000}
                step={1}
                onValueChange={([v]) => setGross(v)}
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>$1</span><span>$3,000</span>
              </div>
            </div>
            <Button onClick={handleConfirmIncome} className="w-full gap-2">
              Confirm income <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* DEDUCTIONS — animated reveal */}
        {step === "deductions" && deductions && (
          <div className="space-y-5">
            <ModuleStepCard
              icon={<TrendingDown className="h-8 w-8 text-primary" />}
              title="Gross → Take-home"
              body={`Watch what comes out before the money hits your account. ${DEDUCTION_NOTE}`}
              action={<div />}
            />
            <div className="rounded-2xl border border-border bg-card/50 p-4 space-y-3">
              <div className="flex items-baseline justify-between">
                <span className="text-xs uppercase tracking-wider text-muted-foreground">Gross pay</span>
                <span className="font-mono text-lg font-semibold text-foreground">${committedGross?.toLocaleString()}</span>
              </div>
              <div className="border-t border-border pt-3 space-y-2">
                <AnimatePresence>
                  {deductions.lines.slice(0, deductionsRevealed).map((line, idx) => {
                    const def = DEDUCTIONS[idx];
                    return (
                      <motion.div
                        key={line.key}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="flex items-center justify-between text-sm"
                      >
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button className="inline-flex items-center gap-1.5 text-left text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:text-foreground">
                              {line.label}
                              <Info className="h-3 w-3" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent
                            side="right"
                            align="start"
                            sideOffset={14}
                            collisionPadding={20}
                            avoidCollisions
                            className="w-[min(360px,calc(100vw-48px))] max-w-[360px] rounded-2xl border border-primary/30 bg-card/80 p-4 text-xs text-foreground shadow-[0_20px_60px_-15px_hsl(var(--primary)/0.45),inset_0_1px_0_hsl(var(--primary)/0.2)] ring-1 ring-primary/10 backdrop-blur-xl"
                          >
                            <p className="font-heading text-sm font-semibold tracking-wide text-foreground">
                              {def.label}
                            </p>
                            <div className="mt-2 h-px w-full bg-gradient-to-r from-primary/40 via-primary/10 to-transparent" />
                            <div className="mt-3 space-y-2 leading-relaxed">
                              <p><span className="font-semibold text-primary">What: </span>{def.description}</p>
                              <p><span className="font-semibold text-primary">Why: </span>{def.why}</p>
                              <p><span className="font-semibold text-primary">Used for: </span>{def.usedFor}</p>
                            </div>
                            <p className="mt-3 border-t border-border/60 pt-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                              Estimated for learning — not your real tax filing.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                        <span className="font-mono text-destructive">−${line.amount}</span>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
              {deductionsRevealed >= deductions.lines.length && (
                <motion.div
                  initial={{ opacity: 0, y: 6, scale: 0.96 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    filter: [
                      "drop-shadow(0 0 0px hsl(var(--primary) / 0))",
                      "drop-shadow(0 0 14px hsl(var(--primary) / 0.55))",
                      "drop-shadow(0 0 0px hsl(var(--primary) / 0))",
                    ],
                  }}
                  transition={{ duration: 0.9, ease: "easeOut" }}
                  className="border-t border-border pt-3 flex items-baseline justify-between"
                >
                  <span className="text-xs uppercase tracking-wider text-muted-foreground">Take-home pay</span>
                  <span className="font-heading text-2xl font-bold text-primary">${deductions.takeHome.toLocaleString()}</span>
                </motion.div>
              )}
            </div>
            <Button
              onClick={handleDeductionsContinue}
              disabled={deductionsRevealed < deductions.lines.length}
              className="w-full gap-2"
            >
              {deductionsRevealed < deductions.lines.length
                ? "Processing paycheck…"
                : "Continue to budget"}
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* ALLOCATE — real categories, no obvious answer reveal */}
        {step === "allocate" && deductions && (
          <div className="space-y-5">
            <ModuleStepCard
              icon={<PieChart className="h-8 w-8 text-primary" />}
              title="Split what's left between savings"
              body={`Your needs and wants are pre-filled at the American household average. You decide how to split the remaining $${Math.max(0, takeHome - (Object.keys(AVG_SHARE) as CategoryKey[]).reduce((s, k) => s + (alloc[k] || 0), 0)).toLocaleString()} between your emergency fund and long-term savings.`}
              action={<div />}
            />
            <div className="rounded-2xl border border-border bg-card/50 p-4 space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Allocated</span>
                <span className="font-mono text-foreground">${totalAllocated} / ${takeHome}</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-muted">
                <div
                  className={`h-full transition-all ${
                    totalAllocated > takeHome ? "bg-destructive" : "bg-primary"
                  }`}
                  style={{ width: `${Math.min(100, (totalAllocated / Math.max(1, takeHome)) * 100)}%` }}
                />
              </div>
              <p className={`text-xs ${remaining < 0 ? "text-destructive" : "text-muted-foreground"}`}>
                {remaining >= 0
                  ? `Remaining: $${remaining}`
                  : `Over budget by $${Math.abs(remaining)}`}
              </p>
            </div>
            <div className="space-y-3">
              {CATEGORIES.map((c) => (
                <div key={c.key} className="rounded-2xl border border-border/60 bg-card/40 p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground">{c.label}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {c.description}
                        {AVG_SHARE[c.key] != null && (
                          <span className="ml-1 text-muted-foreground/70">
                            · US avg {Math.round((AVG_SHARE[c.key] as number) * 100)}%
                          </span>
                        )}
                      </p>
                    </div>
                    {USER_KEYS.includes(c.key) ? (
                      <Input
                        type="number"
                        min={0}
                        max={takeHome}
                        value={alloc[c.key]}
                        onChange={(e) => {
                          const v = Math.max(0, Math.min(takeHome, Number(e.target.value) || 0));
                          setAlloc((prev) => ({ ...prev, [c.key]: v }));
                        }}
                        className="w-24 text-right"
                      />
                    ) : (
                      <div className="w-24 rounded-md border border-border/60 bg-muted/40 px-2 py-1.5 text-right font-mono text-sm text-muted-foreground">
                        ${alloc[c.key].toLocaleString()}
                      </div>
                    )}
                  </div>
                  {USER_KEYS.includes(c.key) && (
                    <Slider
                      className="mt-3"
                      value={[alloc[c.key]]}
                      min={0}
                      max={takeHome}
                      step={5}
                      onValueChange={([v]) => setAlloc((prev) => ({ ...prev, [c.key]: v }))}
                    />
                  )}
                </div>
              ))}
            </div>
            <Button
              onClick={handleAllocCommit}
              className="w-full gap-2"
              disabled={totalAllocated <= 0 || totalAllocated > takeHome}
            >
              Commit budget <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* REVIEW — feedback only AFTER commit */}
        {step === "review_budget" && allocCommitted && (
          <div className="space-y-5">
            <ModuleStepCard
              icon={<ShieldCheck className="h-8 w-8 text-primary" />}
              title="Your budget — feedback"
              body="Now that you've committed, here's how your split compares to a common guideline (50% needs / 30% wants / 20% savings)."
              action={<div />}
            />
            <BucketFeedback bucketTotals={bucketTotals} takeHome={takeHome} />
            <div className="rounded-2xl border border-border bg-card/50 p-4 space-y-1 text-xs">
              <p className="font-semibold text-foreground">Emergency fund this month: ${emergencyFund}</p>
              <p className="text-muted-foreground">
                {emergencyFund < Math.round(takeHome * 0.1)
                  ? "Light reserve — a surprise expense could really hurt."
                  : "Solid reserve — you have some shock-absorbing cushion."}
              </p>
            </div>
            <Button onClick={handleContinueToStress} className="w-full gap-2">
              Continue <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* STRESS — surprise event */}
        {step === "stress" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="space-y-5"
          >
            <ModuleStepCard
              icon={<AlertTriangle className="h-8 w-8 text-destructive" />}
              title={`Surprise: Car repair — $${surpriseAmount}`}
              body="Your car needs an urgent repair. It wasn't in your budget. How you handle this depends on the choices you've already made."
              subtext={
                emergencyFund >= surpriseAmount
                  ? "Good news — your emergency fund can cover this if you choose to use it."
                  : "Tight — your emergency fund alone won't fully cover it."
              }
              action={
                <Button onClick={() => setStep("adjust")} className="w-full gap-2">
                  Decide how to handle it <ArrowRight className="h-4 w-4" />
                </Button>
              }
            />
          </motion.div>
        )}

        {/* ADJUST — real tradeoffs */}
        {step === "adjust" && (
          <div className="space-y-5">
            <div className="rounded-2xl border border-border bg-card/50 p-4 space-y-2 text-sm">
              <SummaryRow label="Take-home pay" value={`$${takeHome}`} />
              <SummaryRow label="Surprise expense" value={`-$${surpriseAmount}`} />
              <SummaryRow label="Emergency fund available" value={`$${emergencyFund}`} />
              <SummaryRow label="Wants budget" value={`$${bucketTotals.wants}`} />
              <SummaryRow label="Savings budget" value={`$${alloc.savings || 0}`} />
            </div>
            <p className="text-sm font-semibold text-foreground">How will you handle it?</p>
            <div className="space-y-3">
              <AdjustChoice
                label="Use the emergency fund"
                desc="Short-term: shock absorbed. Long-term: you'll need to rebuild the fund."
                disabled={emergencyFund === 0}
                disabledReason={emergencyFund === 0 ? "You allocated $0 to emergency this month." : undefined}
                onClick={() => handleChooseAdjustment("emergency")}
              />
              <AdjustChoice
                label="Cut wants this month"
                desc="Short-term: less fun. Long-term: savings intact."
                disabled={bucketTotals.wants < surpriseAmount * 0.5}
                disabledReason={bucketTotals.wants < surpriseAmount * 0.5 ? "Not enough in wants to make a dent." : undefined}
                onClick={() => handleChooseAdjustment("cut_wants")}
              />
              <AdjustChoice
                label="Reduce savings this month"
                desc="Short-term: easy. Long-term: future goals slip."
                onClick={() => handleChooseAdjustment("reduce_savings")}
              />
              <AdjustChoice
                label="Put it on debt"
                desc="Short-term: instant fix. Long-term: interest compounds against you."
                onClick={() => handleChooseAdjustment("debt")}
              />
              <AdjustChoice
                label="Split across categories"
                desc="Spreads pain — partial hit to emergency, wants, and savings."
                onClick={() => handleChooseAdjustment("split")}
              />
            </div>
          </div>
        )}

        {/* OUTCOME */}
        {step === "outcome" && outcomeData && (
          <div className="space-y-5">
            <ModuleStepCard
              icon={outcomeData.debt > 0
                ? <XCircle className="h-8 w-8 text-destructive" />
                : <CheckCircle2 className="h-8 w-8 text-primary" />}
              title={outcomeData.debt > 0 ? "You absorbed the hit — with debt" : "You handled it cleanly"}
              body={outcomeData.summary.join(" ")}
              action={<div />}
            />
            <div className="rounded-2xl border border-border bg-card/50 p-4 space-y-2 text-sm">
              <SummaryRow label="Emergency fund left" value={`$${outcomeData.net_emergency}`} />
              <SummaryRow label="Savings remaining" value={`$${outcomeData.net_savings}`} />
              <SummaryRow label="Wants left" value={`$${outcomeData.net_wants}`} />
              <SummaryRow
                label="New debt"
                value={`$${outcomeData.debt}`}
                tone={outcomeData.debt > 0 ? "negative" : "neutral"}
              />
            </div>
            <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
              These account changes are <b>not</b> applied to your real balances yet. You must pass
              the knowledge check (4/5) to lock them in.
            </div>
            <Button onClick={() => setStep("quiz")} className="w-full gap-2">
              Take the knowledge check <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* QUIZ */}
        {step === "quiz" && (
          <div className="space-y-3">
            <ModuleQuiz
              moduleKey="budget_builder"
              onPass={handleQuizPass}
              backHref={backHref}
              backLabel={backLabel}
            />
            <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
              Need 4/5 to pass. If you don't pass, simulated money from this attempt is voided and
              you'll need to retake the module to earn credit.
            </div>
          </div>
        )}

        {/* SUMMARY */}
        {step === "summary" && (
          <div className="space-y-6 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10">
              {applying
                ? <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                : <Sparkles className="h-8 w-8 text-primary" />}
            </div>
            <h2 className="font-heading text-xl font-bold text-foreground">Budget Plan Complete</h2>
            <p className="text-sm text-muted-foreground">
              Your account balances have been updated based on your decisions. Locked accounts
              still received your allocations — the money is waiting until they unlock.
            </p>
            {accounts.some((a) => a.status === "locked") && (
              <div className="rounded-2xl border border-border bg-card/50 p-3 text-left text-xs text-muted-foreground">
                <div className="mb-1 flex items-center gap-2 text-foreground">
                  <Lock className="h-3.5 w-3.5" /> <span className="font-semibold">Locked accounts hold pending balance</span>
                </div>
                Funds you allocated to a locked account are tracked. You'll see them the moment
                that account unlocks.
              </div>
            )}
            <div className="flex flex-col gap-3 pt-2">
              <Button onClick={() => navigate("/finances")} className="w-full gap-2">
                View Finances <ArrowRight className="h-4 w-4" />
              </Button>
              <Button variant="outline" onClick={() => navigate(backHref)}>{backLabel}</Button>
            </div>
          </div>
        )}
      </TooltipProvider>
    </ModuleShell>
  );
};

/* ---- small presentational helpers ---------------------------------------- */

function SummaryRow({ label, value, tone }: { label: string; value: string; tone?: "negative" | "neutral" }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-mono font-semibold ${tone === "negative" ? "text-destructive" : "text-foreground"}`}>
        {value}
      </span>
    </div>
  );
}

function BucketFeedback({
  bucketTotals, takeHome,
}: { bucketTotals: { needs: number; wants: number; savings: number }; takeHome: number }) {
  const pct = (n: number) => takeHome > 0 ? Math.round((n / takeHome) * 100) : 0;
  const rows = [
    { key: "needs",   label: "Needs",   pct: pct(bucketTotals.needs),   ideal: 50, note: (p: number) => p > 65 ? "Heavy on needs — little room to save." : p < 35 ? "Surprisingly low — make sure essentials are truly covered." : "Reasonable share for essentials." },
    { key: "wants",   label: "Wants",   pct: pct(bucketTotals.wants),   ideal: 30, note: (p: number) => p > 40 ? "Lots of room to enjoy — but less cushion later." : p < 10 ? "Tight — sustainable budgets allow some joy." : "Balanced." },
    { key: "savings", label: "Savings", pct: pct(bucketTotals.savings), ideal: 20, note: (p: number) => p < 10 ? "Low savings — surprises will hurt." : p > 30 ? "Aggressive saver — strong long-term move." : "Healthy save rate." },
  ];
  return (
    <div className="space-y-2">
      {rows.map((r) => (
        <div key={r.key} className="rounded-2xl border border-border bg-card/50 p-3">
          <div className="flex items-baseline justify-between">
            <span className="text-sm font-semibold text-foreground">{r.label}</span>
            <span className="font-mono text-sm text-foreground">{r.pct}%</span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{r.note(r.pct)}</p>
        </div>
      ))}
    </div>
  );
}

function AdjustChoice({
  label, desc, onClick, disabled, disabledReason,
}: { label: string; desc: string; onClick: () => void; disabled?: boolean; disabledReason?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full rounded-2xl border border-border bg-card/50 p-3 text-left transition-colors hover:border-primary/40 disabled:cursor-not-allowed disabled:opacity-50"
    >
      <p className="text-sm font-semibold text-foreground">{label}</p>
      <p className="mt-1 text-xs text-muted-foreground">{desc}</p>
      {disabled && disabledReason && (
        <p className="mt-2 text-[11px] text-destructive">{disabledReason}</p>
      )}
    </button>
  );
}

export default BudgetBuilderModule;